import { i18n } from "@/lib/i18n";
import { nl } from "@/lib/i18n/locales/nl";
import { isRole, roleLabel } from "@/lib/roles";

/** An action the activity log stores (`login`, `admin_invite`, …) that the texts name. */
type KnownAction = keyof typeof nl.activity.actions;

const KNOWN_ACTIONS: ReadonlySet<string> = new Set(Object.keys(nl.activity.actions));

/** Every action the texts name, as the activity log stores it (`admin_revoke_sessions`). */
export const ACTIVITY_ACTIONS: readonly string[] = [...KNOWN_ACTIONS];

function isKnownAction(action: string): action is KnownAction {
  return KNOWN_ACTIONS.has(action);
}

/**
 * An activity log action as people read it, in the dashboard's language:
 * "Overal afgemeld" for `admin_revoke_sessions`. An action the dashboard does not
 * know yet shows as it is stored. The entry's free-text detail is never
 * translated (`activityDetail`): it stays as the backend stored it.
 */
export function activityActionLabel(action: string): string {
  return isKnownAction(action) ? i18n.t(`activity.actions.${action}`) : action;
}

/** The view's actions, whose detail is the viewed person's email or a role as stored (`user`). */
const VIEW_AS_ACTIONS: ReadonlySet<string> = new Set([
  "view_as_start",
  "view_as_switch",
  "view_as_stop",
]);

/**
 * An entry's detail as the log shows it: as the backend stored it, except the
 * role a view showed, which reads as its label in the dashboard's language
 * ("Lid" for `user`); null without one.
 */
export function activityDetail(action: string, detail: string | null | undefined): string | null {
  if (!detail) {
    return null;
  }
  return VIEW_AS_ACTIONS.has(action) && isRole(detail) ? roleLabel(detail) : detail;
}
